import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { CREATEGY_DOMAIN_MESSAGE, isCreategyEmail } from "@/lib/creategy-domain";
import { INVITE_TTL_MS } from "@/lib/admin-invite";
import { isOnboardingCompleteForUser } from "@/lib/onboarding-status";
import {
  auditEventsSchema,
  changeRoleSchema,
  countAdmins,
  ensureCallerIsAdmin,
  finalizeInviteSchema,
  getRoleOf,
  inviteErrorMessage,
  inviteSchema,
  listTeamMembers,
  logInviteAudit,
  loginAuditSchema,
  mirrorRemoteImageForSanitize,
  removeSchema,
  sendResetSchema,
} from "@/lib/admin-members.server";

export type { BackendRole } from "@/lib/admin-invite";

export const getMyAdminStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId, claims } = context;
    const role = await getRoleOf(userId);
    return {
      isAdmin: role === "admin",
      isEditor: role === "editor",
      role,
      userId,
      email: (claims.email as string | undefined) ?? null,
    };
  });

export const listAdmins = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureCallerIsAdmin(context.userId);
    return listTeamMembers();
  });

export const inviteAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => inviteSchema.parse(input))
  .handler(async ({ context, data }) => {
    const emailLc = data.email.trim().toLowerCase();
    try {
      await ensureCallerIsAdmin(context.userId);
      if (!isCreategyEmail(data.email)) throw new Error(CREATEGY_DOMAIN_MESSAGE);

      const redirectTo = `${data.origin.replace(/\/$/, "")}/intern/reset-password?flow=invite`;
      const metaData = {
        ...(data.fullName ? { full_name: data.fullName } : {}),
        ...(data.firstName ? { first_name: data.firstName } : {}),
        ...(data.lastName ? { last_name: data.lastName } : {}),
        invited_role: data.role,
        invited_at: new Date().toISOString(),
      };

      // Look up existing auth user (case-insensitive) so a previously removed or
      // orphaned auth.users row does not block a fresh invite.
      let existingUserId: string | null = null;
      for (let page = 1; page <= 20; page++) {
        const { data: list, error: listErr } = await supabaseAdmin.auth.admin.listUsers({
          page,
          perPage: 200,
        });
        if (listErr) throw new Error(listErr.message);
        const hit = list.users.find((u) => (u.email ?? "").toLowerCase() === emailLc);
        if (hit) {
          existingUserId = hit.id;
          break;
        }
        if (list.users.length < 200) break;
      }

      let userId: string;
      let reinvited = false;

      if (existingUserId) {
        // Block if the user is already an active team member — role changes go
        // through the team list, not the invite form.
        const currentRole = await getRoleOf(existingUserId);
        if (currentRole) {
          throw new Error("Diese Person ist bereits Mitglied — Rolle über die Team-Liste ändern.");
        }

        // Refresh metadata (name / invited_role) for the existing user.
        const { error: updErr } = await supabaseAdmin.auth.admin.updateUserById(existingUserId, {
          user_metadata: metaData,
          email_confirm: true,
        });
        if (updErr) throw new Error(inviteErrorMessage(updErr.message));

        // Existing auth users cannot receive another admin invite; send a
        // password-setup email instead and keep the onboarding copy via flow=invite.
        const { error: resetErr } = await supabaseAdmin.auth.resetPasswordForEmail(data.email, {
          redirectTo,
        });
        if (resetErr) throw new Error(inviteErrorMessage(resetErr.message));

        userId = existingUserId;
        reinvited = true;
      } else {
        const { data: invited, error: inviteErr } = await supabaseAdmin.auth.admin.inviteUserByEmail(
          data.email,
          { data: metaData, redirectTo },
        );
        if (inviteErr) throw new Error(inviteErrorMessage(inviteErr.message));
        const newUserId = invited.user?.id;
        if (!newUserId) throw new Error("Einladung fehlgeschlagen: keine User-ID erhalten");
        userId = newUserId;
      }

      const { error: roleErr } = await supabaseAdmin
        .from("user_roles")
        .upsert({ user_id: userId, role: data.role }, { onConflict: "user_id,role" });
      if (roleErr) throw new Error(roleErr.message);

      await logInviteAudit("ADMIN_INVITE_SUCCESS", {
        email: emailLc,
        role: data.role,
        fullName: data.fullName ?? null,
        reinvited,
        actorUserId: context.userId,
        recordId: userId,
      });

      return { ok: true as const, userId, reinvited };
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      await logInviteAudit("ADMIN_INVITE_REJECTED", {
        email: emailLc,
        role: data.role,
        fullName: data.fullName ?? null,
        reason,
        actorUserId: context.userId,
      });
      throw err;
    }
  });

export const finalizeInvitePassword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => finalizeInviteSchema.parse(input))
  .handler(async ({ context, data }) => {
    const userId = context.userId;
    const { data: fetched, error: getErr } = await supabaseAdmin.auth.admin.getUserById(userId);
    if (getErr || !fetched?.user) {
      throw new Error("Nutzerkonto nicht gefunden.");
    }
    const meta = (fetched.user.user_metadata ?? {}) as Record<string, unknown>;
    const email = (fetched.user.email ?? "").toLowerCase();
    const invitedAtRaw = meta.invited_at;
    const invitedAt = typeof invitedAtRaw === "string" ? Date.parse(invitedAtRaw) : NaN;

    if (data.mode === "invite" && Number.isFinite(invitedAt)) {
      if (Date.now() - invitedAt > INVITE_TTL_MS) {
        await logInviteAudit("INVITE_FINALIZE_REJECTED", {
          email,
          reason: "INVITE_EXPIRED",
          actorUserId: userId,
          recordId: userId,
        });
        throw new Error("INVITE_EXPIRED");
      }
    }

    const { error: updErr } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      password: data.password,
      user_metadata: {
        ...meta,
        invited_at: null,
        password_set_at: new Date().toISOString(),
      },
    });
    if (updErr) {
      await logInviteAudit("INVITE_FINALIZE_REJECTED", {
        email,
        reason: updErr.message,
        actorUserId: userId,
        recordId: userId,
      });
      throw new Error(updErr.message);
    }

    await logInviteAudit(
      data.mode === "reset" ? "INTERNAL_PASSWORD_SET" : "INVITE_FINALIZE_SUCCESS",
      { email, actorUserId: userId, recordId: userId },
    );
    return { ok: true as const };
  });

export const changeMemberRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => changeRoleSchema.parse(input))
  .handler(async ({ context, data }) => {
    await ensureCallerIsAdmin(context.userId);
    if (data.userId === context.userId && data.role !== "admin") {
      throw new Error("Sie können Ihre eigene Admin-Rolle nicht entziehen.");
    }
    const currentRole = await getRoleOf(data.userId);
    if (currentRole === "admin" && data.role !== "admin") {
      const adminCount = await countAdmins();
      if (adminCount <= 1) {
        throw new Error("Der letzte verbleibende Administrator kann nicht herabgestuft werden.");
      }
    }

    // Beide möglichen Rollen löschen, dann die gewünschte einfügen
    const { error: delErr } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.userId)
      .in("role", ["admin", "editor"]);
    if (delErr) throw new Error(delErr.message);

    const { error: insErr } = await supabaseAdmin
      .from("user_roles")
      .upsert({ user_id: data.userId, role: data.role }, { onConflict: "user_id,role" });
    if (insErr) throw new Error(insErr.message);

    return { ok: true };
  });

export const removeMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => removeSchema.parse(input))
  .handler(async ({ context, data }) => {
    await ensureCallerIsAdmin(context.userId);
    if (data.userId === context.userId) {
      throw new Error("Sie können sich nicht selbst entfernen.");
    }
    const currentRole = await getRoleOf(data.userId);
    if (currentRole === "admin") {
      const adminCount = await countAdmins();
      if (adminCount <= 1) {
        throw new Error("Der letzte verbleibende Administrator kann nicht entfernt werden.");
      }
    }
    // Ticket W12, Punkt 4: Ein Konto mit bestehender Zuordnung wird nicht
    // gelöscht. Sonst verschwände die Person unbemerkt von Kurs- und
    // Download-Seiten. Die Datenbank sperrt es ebenfalls (Löschsperre) – hier
    // steht der verständliche Satz dazu.
    const { data: assigned } = await supabaseAdmin
      .from("offer_people")
      .select("services(title), lead_magnets(title)")
      .eq("profile_id", data.userId);
    const assignedOffers = ((assigned ?? []) as Array<Record<string, any>>)
      .map(
        (row) =>
          (row["services"]?.title as string) ?? (row["lead_magnets"]?.title as string) ?? null,
      )
      .filter((title): title is string => Boolean(title));
    if (assignedOffers.length > 0) {
      throw new Error(
        `Diese Person erscheint noch auf folgenden Angeboten: ${assignedOffers.join(", ")}. Bitte entfernen Sie die Zuordnungen dort zuerst, danach lässt sich das Konto löschen.`,
      );
    }

    const { error } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.userId)
      .in("role", ["admin", "editor"]);
    if (error) throw new Error(error.message);

    // Fully delete the auth user so the same email can be re-invited later.
    const { error: delUserErr } = await supabaseAdmin.auth.admin.deleteUser(data.userId);
    if (delUserErr && !/not.?found/i.test(delUserErr.message)) {
      throw new Error(delUserErr.message);
    }
    return { ok: true };
  });

export const sendPasswordResetForMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => sendResetSchema.parse(input))
  .handler(async ({ context, data }) => {
    let email = "";
    try {
      await ensureCallerIsAdmin(context.userId);

      const { data: fetched, error: getErr } = await supabaseAdmin.auth.admin.getUserById(
        data.userId,
      );
      if (getErr) throw new Error(getErr.message);
      const user = fetched.user;
      if (!user?.email) throw new Error("Nutzer nicht gefunden.");
      email = user.email.toLowerCase();

      const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
      const onboardingComplete = isOnboardingCompleteForUser(user);
      const flow: "reset" | "invite" = onboardingComplete ? "reset" : "invite";
      const origin = data.origin.replace(/\/$/, "");
      const redirectTo = `${origin}/intern/reset-password?flow=${flow}`;

      // Für offene Onboardings TTL neu setzen, damit der 30-Minuten-Timer greift.
      if (flow === "invite") {
        const { error: updErr } = await supabaseAdmin.auth.admin.updateUserById(user.id, {
          user_metadata: {
            ...meta,
            invited_at: new Date().toISOString(),
          },
        });
        if (updErr) throw new Error(inviteErrorMessage(updErr.message));
      }

      const { error: resetErr } = await supabaseAdmin.auth.resetPasswordForEmail(user.email, {
        redirectTo,
      });
      if (resetErr) throw new Error(inviteErrorMessage(resetErr.message));

      await logInviteAudit("ADMIN_PASSWORD_LINK_SENT", {
        email,
        actorUserId: context.userId,
        recordId: user.id,
        reason: flow,
      });

      return { ok: true as const, flow };
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      await logInviteAudit("ADMIN_PASSWORD_LINK_REJECTED", {
        email,
        actorUserId: context.userId,
        recordId: data.userId,
        reason,
      });
      throw err;
    }
  });

export const listLoginAuditEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { limit?: number; sinceHours?: number }) =>
    loginAuditSchema.parse(input ?? {}),
  )
  .handler(async ({ context, data }) => {
    await ensureCallerIsAdmin(context.userId);
    const limit = data.limit ?? 200;
    const sinceHours = data.sinceHours ?? 24 * 7;
    const since = new Date(Date.now() - sinceHours * 60 * 60 * 1000).toISOString();

    const { data: rows, error } = await supabaseAdmin
      .from("login_audit_log")
      .select(
        "id, event_type, email, email_domain, ip, user_agent, failed_count, window_started_at, locked_until, reason, created_at",
      )
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return { events: rows ?? [] };
  });

export const listAuditEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      limit?: number;
      sinceHours?: number;
      table?: string | null;
      action?: string | null;
      actorId?: string | null;
    }) => auditEventsSchema.parse(input ?? {}),
  )
  .handler(async ({ context, data }) => {
    await ensureCallerIsAdmin(context.userId);
    const limit = data.limit ?? 100;
    const sinceHours = data.sinceHours ?? 24 * 7;
    const since = new Date(Date.now() - sinceHours * 60 * 60 * 1000).toISOString();

    let query = supabaseAdmin
      .from("audit_log")
      .select(
        "id, occurred_at, actor_id, actor_email, actor_role, action, table_name, record_id, changed_columns, old_data, new_data, context, ip, user_agent",
      )
      .gte("occurred_at", since)
      .order("occurred_at", { ascending: false })
      .limit(limit);

    if (data.table) query = query.eq("table_name", data.table);
    if (data.action) query = query.eq("action", data.action);
    if (data.actorId) query = query.eq("actor_id", data.actorId);

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);

    // Log the bulk read itself for accountability.
    await (await import("@/lib/audit.server")).writeAuditEvent({
      action: "READ_BULK",
      tableName: "audit_log",
      newData: {
        filters: {
          sinceHours,
          table: data.table ?? null,
          action: data.action ?? null,
          actorId: data.actorId ?? null,
        },
        result_count: rows?.length ?? 0,
      },
      context: "serverfn:listAuditEvents",
      actorId: context.userId,
    });

    return { events: rows ?? [] };
  });

// ---- Outrank: Bestehende Beiträge nachträglich bereinigen ------------------
// Entfernt Inline-TOC + rohe HTML-Anker aus allen blog_posts und spiegelt
// externe Bild-URLs nach blog-inline. Idempotent – kann beliebig oft laufen.
export const sanitizeExistingOutrankPosts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureCallerIsAdmin(context.userId);

    const { sanitizeOutrankMarkdown, findExternalImageUrls, replaceImageUrl } = await import(
      "@/lib/outrank/sanitize"
    );

    const supabaseUrl = process.env.SUPABASE_URL;

    const { data: rows, error } = await supabaseAdmin
      .from("blog_posts")
      .select("id, slug, content_md");
    if (error) throw new Error(error.message);

    let sanitized = 0;
    let mirrored = 0;
    const sampleSlugs: string[] = [];

    for (const row of rows ?? []) {
      const original = row.content_md ?? "";
      let content = sanitizeOutrankMarkdown(original);
      let didMirror = 0;

      if (supabaseUrl) {
        const externals = findExternalImageUrls(content);
        for (const url of externals) {
          const path = await mirrorRemoteImageForSanitize(url, row.slug);
          if (!path) continue;
          const encoded = path
            .split("/")
            .map((seg) => encodeURIComponent(seg))
            .join("/");
          const target = `${supabaseUrl.replace(/\/+$/, "")}/storage/v1/object/sign/blog-inline/${encoded}`;
          const next = replaceImageUrl(content, url, target);
          if (next !== content) {
            content = next;
            didMirror++;
          }
        }
      }

      if (content !== original) {
        const { error: upErr } = await supabaseAdmin
          .from("blog_posts")
          .update({ content_md: content, updated_at: new Date().toISOString() })
          .eq("id", row.id);
        if (upErr) throw new Error(upErr.message);
        sanitized++;
        mirrored += didMirror;
        if (sampleSlugs.length < 20) sampleSlugs.push(row.slug);
      }
    }

    return {
      scanned: rows?.length ?? 0,
      sanitized,
      mirrored,
      sample_slugs: sampleSlugs,
    };
  });
